import React, { useEffect, useMemo, useRef, useState } from 'react';

import { orderBy } from 'lodash';

import { Dropdown } from 'components/common/TreeCrumbs/Dropdown/Dropdown';

import { NewReportModal } from 'components/Dashboards/Reporting/AddToReport/components/newReportModal/NewReportModal';
import { ReportListApiResponse } from 'components/Dashboards/Reporting/reporting.types';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

import classes from './ReportsListDropdown.module.scss';

interface Props {
    dropdownAnchorRef: HTMLElement | null;
    isDropdownOpen: boolean;
    setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
    reportsList: ReportListApiResponse[];
    onAddWidgetToReport: (reportName: string, reportId: number) => void;
    onCreateNewReport: (reportName: string) => Promise<void>;
    isLoadingCreateNewReport: boolean;
}

export const ReportsListDropdown = ({
    dropdownAnchorRef,
    isDropdownOpen,
    setIsDropdownOpen,
    reportsList,
    onAddWidgetToReport,
    onCreateNewReport,
    isLoadingCreateNewReport,
}: Props) => {
    const [search, setSearch] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const listRef = useRef<HTMLDivElement>(null);
    const [isCreateNewReportModalOpen, setIsCreateNewReportModalOpen] = useState<boolean>(false);

    const orderedReports = useMemo(() => {
        return orderBy(reportsList, (report) => report.name?.toLowerCase(), 'asc');
    }, [reportsList]);

    const filteredReports = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return orderedReports;
        return orderedReports.filter((report) => report.name?.toLowerCase().includes(query));
    }, [orderedReports, search]);

    const onSelectReport = (report: ReportListApiResponse) => {
        onAddWidgetToReport(report.name, report.id);
        setIsDropdownOpen(false);
    };

    // Reset focused index when search changes
    useEffect(() => {
        setFocusedIndex(-1);
    }, [search]);

    // Reset focused index when dropdown opens
    useEffect(() => {
        if (isDropdownOpen) {
            setFocusedIndex(-1);
            setSearch('');
        }
    }, [isDropdownOpen]);

    // Auto-scroll to focused item when navigating with arrow keys
    useEffect(() => {
        if (focusedIndex >= 0 && listRef.current) {
            const focusedElement = listRef.current.children[focusedIndex] as HTMLElement;
            if (focusedElement) {
                focusedElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }
    }, [focusedIndex]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!filteredReports.length) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex((prev) => (prev < filteredReports.length - 1 ? prev + 1 : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex((prev) => (prev > 0 ? prev - 1 : filteredReports.length - 1));
                break;
            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredReports.length) {
                    onSelectReport(filteredReports[focusedIndex]);
                }
                break;
            case 'Escape':
                setIsDropdownOpen(false);
                break;
        }
    };

    const handleSearchFocus = () => {
        setFocusedIndex(-1);
    };

    const handleOnCreateNewReport = () => {
        setIsCreateNewReportModalOpen(true);
        setIsDropdownOpen(false);
    };

    return (
        <>
            <Dropdown anchor={dropdownAnchorRef} handleClose={() => setIsDropdownOpen(false)} isOpen={isDropdownOpen}>
                <div className={classes.dropdownContent}>
                    <div className={classes.title}>Add to report:</div>

                    <TextInput
                        value={search}
                        onClear={() => setSearch('')}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={handleSearchFocus}
                        onKeyDown={handleKeyDown}
                        autoFocus={true}
                        placeholder={'Search reports...'}
                    />

                    <div className={classes.reportListWrapper} ref={listRef}>
                        {filteredReports.length > 0 &&
                            filteredReports.map((report, index) => (
                                <div
                                    className={`${classes.listContainer} ${focusedIndex === index ? classes.focused : ''}`}
                                    key={report.id}
                                    id={`${report.id}_${index}`}
                                    onClick={() => onSelectReport(report)}
                                >
                                    <span data-testid={'items-list'} className={classes.listItem}>
                                        {report.name}
                                    </span>
                                </div>
                            ))}
                        {filteredReports.length === 0 && <div className={classes.emptyState}>No reports found</div>}
                    </div>

                    <div className={classes.createNewReportWrapper}>
                        <div className={classes.separatorDiv} />
                        <div className={classes.createNewReport} onClick={handleOnCreateNewReport}>
                            + Create new report
                        </div>
                    </div>
                </div>
            </Dropdown>
            {isCreateNewReportModalOpen && (
                <NewReportModal
                    isModalOpen={isCreateNewReportModalOpen}
                    setIsModalOpen={setIsCreateNewReportModalOpen}
                    reportsList={reportsList}
                    onSubmit={onCreateNewReport}
                    isLoading={isLoadingCreateNewReport}
                />
            )}
        </>
    );
};
